import LZUTF8 from 'lzutf8';
import * as Form from './finalthesis';

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';

export const FORM_NAME = 'umr-fb12-finalthesis';
export const FORM_VERSION = '1.0.1';
export const FORM_DATE = '2025-02-06';

export const EMPTY_FORM_DATA : Form.AnmeldungDerAbschlussarbeit = {
    requirements_bsc_cs_23: {},
    requirements_bsc_ds_23: {},
    requirements_bsc_bi_23: {},
    requirements_bsc_m_23: {},
    requirements_bsc_bm_23: {},
    requirements_msc_cs_23: {},
    requirements_msc_ds_23: {},
    requirements_msc_bi_23: {},
    requirements_msc_m_23: {},
    requirements_msc_bm_23: {},
    requirements_bsc_cs_18: {},
    requirements_bsc_ds_18: {},
    requirements_bsc_bi_18: {},
    requirements_bsc_m_18: {},
    requirements_bsc_bm_18: {},
    requirements_msc_cs_18: {},
    requirements_msc_ds_18: {},
    requirements_msc_bi_18: {},
    requirements_msc_m_18: {},
    requirements_msc_bm_18: {},
    requirements_other: {},
    lastname: undefined,
    firstname: undefined,
    studentid: undefined,
    birthday: undefined,
    birthplace: undefined,
    email: undefined,
    phone: undefined,
    username: undefined,
    subject: undefined,
    degree: undefined,
    exam_regulations: undefined,
    supervisor: undefined,
    second_supervisor: undefined,
    title: undefined,
    start_date: undefined,
    comment: undefined
};

export type FormKey = keyof Form.AnmeldungDerAbschlussarbeit;

export function isJSONSchema7(schema: JSONSchema7Definition): schema is JSONSchema7 {
	return typeof schema !== "boolean"; 
}

type KeyMapping = {
	[k: string]: string;
}

const subject_keys: KeyMapping = {
	"Informatik / Computer Science": "cs",
	"Data Science": "ds",
	"Wirtschaftsinformatik / Business Informatics": "bi",
	"Mathematik / Mathematics": "m",
	"Wirtschaftsmathematik / Business Mathematics": "bm"
};

const degree_keys: KeyMapping = {
	"Bachelor": "bsc",
	"Master": "msc"
};

const exam_regulations_keys: KeyMapping = {
	"Ab Sommer 2018": "18",
	"Ab Winter 2023/24": "23"
};

const known_profs : KeyMapping = {
	"Prof. Dr. Ilka Agricola": "Agricola, Ilka, Prof. Dr.",
    "Prof. Dr. Paul Alpar": "Alpar, Paul, Prof. Dr.",
	"Prof. Dr. Thomas Bauer": "Bauer, Thomas, Prof. Dr.",
    "Prof. Dr. Christoph Bockisch": "Bockisch, Christoph, Prof. Dr.",
	"Prof. Dr. Daniel Braun": "Braun, Daniel, Prof. Dr.",
    "Prof. Dr. Bernd Freisleben": "Freisleben, Bernd, Prof. Dr.",
    "Prof. Dr. Oliver Goertsches": "Goertsches, Oliver, Prof. Dr.",
    "Prof. Dr. István Heckenberger": "Heckenberger, István, Prof. Dr.",
    "Prof. Dr. Hajo Holzmann": "Holzmann, Hajo, Prof. Dr.",
    "Prof. Dr. Patrick Mehlitz": "Mehlitz, Patrick, Prof. Dr.",
    "Prof. Dr. Thorsten Papenbrock": "Papenbrock, Thorsten, Prof. Dr.",
    "Prof. Dr. Pablo Ramacher": "Ramacher, Pablo, Prof. Dr.",
    "Prof. Dr. Christian Rieger": "Rieger, Christian, Prof. Dr.",
    "Prof. Dr. Sönke Rollenske": "Rollenske, Sönke, Prof. Dr.",
    "Prof. Dr. Bernhard Seeger": "Seeger, Bernhard, Prof. Dr.",
    "Prof. Dr. Christin Seifert": "Seifert, Christin, Prof. Dr.",
    "Prof. Dr. Gabriele Taentzer": "Taentzer, Gabriele, Prof. Dr.",
    "Prof. Dr. Thorsten Thormählen": "Thormählen, Thorsten, Prof. Dr.",
    "Prof. Dr. Elmar Tischhauser": "Tischhauser, Elmar, Prof. Dr.",
    "Prof. Dr. Volkmar Welker": "Welker, Volkmar, Prof. Dr.",
    "Prof. Dr. Sebastian Wild": "Wild, Sebastian, Prof. Dr."
}

const known_exam_regulations: KeyMapping = {
	"Ab Sommer 2018": "20181",
	"Ab Winter 2023/24": "20232"
};


export const LOCAL_STORE_KEY_FORM_DATA = 'formData';
export const LOCAL_STORE_KEY_LANG = 'lang';

export function getInitialFormData() : Form.AnmeldungDerAbschlussarbeit {
	var parameterValue = new URLSearchParams(document.location.search).get(LOCAL_STORE_KEY_FORM_DATA);
	var localStoreageValue = localStorage.getItem(LOCAL_STORE_KEY_FORM_DATA);

	if (parameterValue !== undefined && parameterValue !== null) {
		var compressedFormData = decodeURIComponent(parameterValue);
		return JSON.parse(LZUTF8.decompress(compressedFormData, { inputEncoding: 'Base64' }));
	} else if (localStoreageValue !== undefined && localStoreageValue !== null) {
		return JSON.parse(localStoreageValue);
	} else {
		return EMPTY_FORM_DATA;
	}
}

export function getInitialLang() : string {
	var parameterValue = new URLSearchParams(document.location.search).get(LOCAL_STORE_KEY_LANG);
	var localStoreageValue = localStorage.getItem(LOCAL_STORE_KEY_LANG);

	if (parameterValue !== undefined && parameterValue !== null) {
		return parameterValue;
	} else if (localStoreageValue !== undefined && localStoreageValue !== null) {
		return localStoreageValue;
	} else {
		return 'de';
	}
}

export function editLink(formData : Form.AnmeldungDerAbschlussarbeit, lang : string) {
	const formDataCompressed = LZUTF8.compress(JSON.stringify(formData), { outputEncoding: 'Base64' });
	const editLink = document.location.origin + document.location.pathname + '?lang=' + lang + '&formData=' + encodeURIComponent(formDataCompressed);
	return editLink;
}

export function csvData(formData : Form.AnmeldungDerAbschlussarbeit, subject : string | undefined, degree : string | undefined, exam_regulations : string | undefined, lang : string, requirementsFulfilled : boolean) {
	// when changing the csv representation, at least increase the minor version number of FORM_VERSION
	const agreeAlumni = formData.agreement_alumni === true;
	const supervisor = known_profs[getSupervisorName(formData) ?? ""] ?? getSupervisorName(formData);
	const secondSupervisor = known_profs[getSecondSupervisorName(formData) ?? ""] ?? getSecondSupervisorName(formData);
	const exam_regulations_short = known_exam_regulations[exam_regulations ?? ""] ?? exam_regulations;

	const titlesDeEn = [];
	if (subject && subject.indexOf('/') > -1) {
		const split = subject.split('/');
		titlesDeEn[0] = split[0];
		titlesDeEn[1] = split[1];
	} else if (subject) {
		titlesDeEn[0] = subject;
		titlesDeEn[1] = subject;
	} else {
		titlesDeEn[0] = "<missing>";
		titlesDeEn[1] = "<missing>";
	}
	const subject_official = degree === 'Bachelor' ? titlesDeEn[0] : titlesDeEn[1];

	
	const formDataEncoded =
		// CSV headers
		// Prüfer	AG	Zweitprüfer	Matrikelnummer	Name	Vorname	Studiengang	Abschluss	PO	Thema	Ausgabe Thema	Abgabedatum	Verlängerung	2. Verlängerung	tatsächliche Abgabe	Termin Erstgutachten	Eingang Erstgutachten	Disputation	Datum Abschluss	Zeugnis ausgestellt	Zeugnisausgabe	E-Mail	Private E-Mail	Telefon	Zustimmung Alumni	Heimatadresse	Bevorzugte Sprache	Geburtstag	Geburtsort	Zulassungsvoraussetzungen	Kommentar	Formular Version
		excelEscape(orEmpty(supervisor)) + '\t' +
		'\t' + // AG
		excelEscape(orEmpty(secondSupervisor)) + '\t' +
		excelEscape(orEmpty(formData.studentid)) + '\t' +
		excelEscape(orEmpty(formData.lastname)) + '\t' +
		excelEscape(orEmpty(formData.firstname)) + '\t' +
		excelEscape(orEmpty(subject_official)) + '\t' +
		excelEscape(orEmpty(degree)) + '\t' +
		excelEscape(orEmpty(exam_regulations_short)) + '\t' +
		excelEscape(orEmpty(formData.title)) + '\t' +
		excelEscape(orEmpty(formData.start_date)) + '\t' +
		'\t' + // Abgabedatum
		'\t' + // Verlängerung
		'\t' + // 2. Verlängerung
		'\t' + // tatsächliche Abgabe
		'\t' + // Termin Erstgutachten
		'\t' + // Eingang Erstgutachten
		'\t' + // Disputation
		'\t' + // Datum Abschluss
		'\t' + // Zeugnis ausgestellt
		'\t' + // Zeugnisausgabe
		excelEscape(orEmpty(formData.username + '@students.uni-marburg.de')) + '\t' + // offizielle E-Mail
		excelEscape(orEmpty(agreeAlumni ? formData.email : undefined)) + '\t' + // Private E-Mail
		excelEscape(orEmpty(agreeAlumni ? formData.phone : undefined)) + '\t' +  // Telefon
		excelEscape((agreeAlumni ? 'TRUE' : 'FALSE')) + '\t' + // Zustimmung Datenspeicherung
		excelEscape(orEmpty(agreeAlumni ? formData.home_address : undefined)) + '\t' +  // Heimatadresse
		excelEscape(orEmpty(lang)) + '\t' +  // Bevorzugte Sprache
		excelEscape(orEmpty(formData.birthday)) + '\t' + // Geburtstag
		excelEscape(orEmpty(formData.birthplace)) + '\t' + // Geburtsort
		excelEscape(orEmpty(requirementsFulfilled ? 'zugelassen' : 'nicht zugelassen')) + '\t' + // Zulassungsvoraussetzungen erfüllt
		excelEscape(orEmpty(formData.comment)) + '\t' + // Kommentar
		excelEscape(FORM_NAME + '_' + FORM_VERSION + '_' + FORM_DATE);

	return formDataEncoded;
}

function orEmpty(value: string | number | undefined) {
	if (value === undefined)
		return '';
	else if (typeof value === "number")
		return value.toString();
	else
		return value;
}

function excelEscape(string: string) {
	return '\'' + string;
}
export function getSupervisorName(formData : Form.AnmeldungDerAbschlussarbeit) {
	return formData.supervisor === 'Andere(r)' ? formData.supervisor_other : formData.supervisor;
}

export function getSecondSupervisorName(formData : Form.AnmeldungDerAbschlussarbeit) {
	return formData.second_supervisor === 'Andere(r)' ? formData.second_supervisor_other : formData.second_supervisor;
}

export function getSubject(formData : Form.AnmeldungDerAbschlussarbeit) {
	return formData.subject;	
}

export function getDegree(formData : Form.AnmeldungDerAbschlussarbeit) {
	return formData.degree;	
}

export function getExamRegulations(formData : Form.AnmeldungDerAbschlussarbeit) {
	return formData.exam_regulations === 'Andere' ? formData.exam_regulations_other : formData.exam_regulations;
}

export function getRequirementsPropertyName(formData : Form.AnmeldungDerAbschlussarbeit) {
	var subject = getSubject(formData);
	var degree = getDegree(formData);
	var exam_regulations = getExamRegulations(formData);

	var subject_key = undefined;
	var degree_key = undefined;
	var exam_regulations_key = undefined;


	if (subject !== undefined) {
		subject_key = subject_keys[subject];
	} else {
		return undefined;
	}
	
	if (degree !== undefined) {
		degree_key = degree_keys[degree];
	} else {
		return undefined;
	}
	if (exam_regulations !== undefined) {
		exam_regulations_key = exam_regulations_keys[exam_regulations];
	} else {
		return undefined;
	}
		
	return "requirements_" + degree_key + "_" + subject_key + "_" + exam_regulations_key;
}

export function formatDate(date : Date | undefined) {
	if (date === undefined)
		return undefined;
	let day = date.getDate();
	let month = date.getMonth() + 1;
	let year = date.getFullYear();
	return year + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
}
